import { mergeMap,  first, tap, filter, flatMap } from 'rxjs/operators';
import { Component, OnDestroy, Input, Output, EventEmitter, OnChanges, SimpleChanges } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Subscription, Subject } from "rxjs";
import { EventService, ItemService, PermissionService, StreamService, TenantService, UserService } from '../services';
import { ItemContextModel } from '../model';
import { MediaObserver } from "@angular/flex-layout";
import * as _ from 'lodash';
import { DomSanitizer } from '@angular/platform-browser';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ConfirmationDialogComponent } from '../general/confirmationDialog.component';
import { CopyItemDialogComponent } from '../summary/copyItemDialog.component';

@Component({
    selector: 'item-viewer',
    styles: [`
        .item-origin {
            font-weight: bold;
        }
        
        .item-date {
            color: grey;
        }

        .item-details {
            overflow-y: auto;
        }

        .main-contents {
            padding: 20px;
            font-size: 18px;
            line-height: 24px;
        }

        mat-toolbar.mat-toolbar {
            background-color: inherit;
        }

        .large-item-details {
            height: calc(100vh - 128px);
        }
        
        .small-item-details {
            height: calc(100vh - 128px);
        }

        .warning-panel {
            background-color: #D7D7D7; 
            padding: 15px;
        }

        .warning-icon {
            padding-right: 15px; 
            padding-bottom: 10px; 
            margin-top: -5px;
        }
    `],
    template: `
        <div *ngIf="itemContext?.item" fxLayoutAlign="center center">
            <div class="item-contents" fxFlex="824px" fxFlex.lt-md="100">
                <mat-toolbar>
                    <span *ngIf="isReadable() && !curating"
                          [matTooltip]="adjustedTooltip(itemContext?.item?.savedByUser ? 'Unsave' : 'Save')"
                          matTooltipPosition="below">
                        <button mat-icon-button
                                [disabled]="!currentUser()"      
                                (click)="toggleSaved()">
                            <i class="material-icons">{{itemContext?.item?.savedByUser ? 'bookmark' : 'bookmark_border'}}</i>
                        </button>
                    </span>
                    <span *ngIf="showFullTextLink() && isReadable() && !curating"
                          [matTooltip]="adjustedTooltip('Show Full Text')"
                          matTooltipPosition="below">
                        <button [disabled]="!currentUser() || fullTextLinkDisabled()"
                                mat-icon-button
                                (click)="getFullArticleText()">
                            <i class="material-icons">select_all</i>
                        </button>
                    </span>
                    <span *ngIf="isReadable() && !curating"
                          [matTooltip]="adjustedTooltip('Share')"
                          matTooltipPosition="below">
                        <button mat-icon-button 
                                [disabled]="!currentUser()"
                                (click)="emailShare()">
                            <i class="material-icons">share</i>
                        </button>
                    </span>
                    <span *ngIf="canCopy && !curating"
                          [matTooltip]="adjustedTooltip('Copy')"
                          matTooltipPosition="below">
                        <button mat-icon-button 
                                [disabled]="!currentUser()"
                                (click)="copy()">
                            <i class="material-icons">file_copy</i>
                        </button>
                    </span>
                    <span *ngIf="isReadable() && !curating"
                          [matTooltip]="adjustedTooltip(itemContext?.item?.likedByUser ? 'Unlike' : 'Like')"
                          matTooltipPosition="below">
                        <button mat-icon-button 
                                [ngClass]="{'active-thumb': itemContext?.item?.likedByUser}"
                                [disabled]="!currentUser()"
                                (click)="toggleLikedBy()">
                            <i class="material-icons">thumb_up</i>
                        </button>
                    </span>
                    <span *ngIf="currentUser() && isReadable() && itemContext?.item?.likesCount" [matTooltip]="(itemContext?.item?.likesCount || 0) + ' likes'">({{itemContext?.item?.likesCount || 0}})</span>
                    <button *ngIf="currentUser() && isReadable()"
                            mat-icon-button 
                            [ngClass]="{'active-thumb': showTags}"
                            [matTooltip]="showTags ? 'Hide Tags' : 'Show Tags'"
                            matTooltipPosition="below"
                            (click)="showTags = !showTags">
                        <i class="material-icons">label</i>
                    </button>
                    <button *ngIf="isDeleteable"
                            mat-icon-button
                            matTooltip="Delete"
                            matTooltipPosition="below"
                            (click)="deleteItem()">
                        <i class="material-icons">delete</i>
                    </button>
                    <button *ngIf="isEditable"
                            mat-icon-button 
                            matTooltip="Edit"
                            matTooltipPosition="below"
                            (click)="editItem = !editItem">
                        <i class="material-icons">edit</i>
                    </button>
                    <button *ngIf="currentUser() && canEditTopic"
                            mat-icon-button 
                            [matTooltip]="itemContext.item?.visible ? 'Hide item' : 'Show item'"
                            (click)="toggleVisibility()">
                        <mat-icon>{{itemContext.item?.visible ? 'visibility' : 'visibility_off'}}</mat-icon>
                    </button>
                    <button *ngIf="currentUser() && canEditTopic && curating"
                            mat-icon-button 
                            [matTooltip]="itemContext.item?.inCurationItems ? 'Remove from training set' : 'Add to training set'"
                            (click)="toggleCurationItem()">
                            <mat-icon fontSet="far" [fontIcon]="itemContext.item?.inCurationItems ? 'fa-check-circle' : 'fa-circle'"></mat-icon>
                    </button>
                    <span fxFlex></span>
                    <button mat-icon-button (click)="close.emit()">
                        <mat-icon>clear</mat-icon>
                    </button>
                </mat-toolbar>
                <div class="item-details" [ngClass]="{ 'large-item-details': !isSmall(), 'small-item-details': isSmall() }">
                    <item-summary [item]="itemContext.item" 
                                  [noLink]="true"
                                  [topicId]="true"
                                  [isAdmin]="canEditTopic"
                                  [detailSummary]="true"
                                  [curating]="curating"
                                  [visibilityDisabled]="isSource"
                                  [disableTitle]="accessRestricted"
                                  (externalLinkClicked)="externalLinkClicked()"></item-summary>
                    <span *ngIf="!editItem">
                        <mat-card *ngIf="accessRestricted && isSubscribable">
                            <mat-card-content fxLayoutAlign="start center">
                                <div>You must register to read {{prodSource.title}} to access this item. <button mat-button (click)="subscribeToProdSource()">Register here</button></div>
                            </mat-card-content>
                        </mat-card>
                        <mat-card *ngIf="accessRestricted && !isSubscribable">
                            <mat-card-content fxLayoutAlign="start center">
                                <div *ngIf="!productOversubscribedMessage">Registration for {{prodSource.title}} is full, please contact your site administrator for access.</div> 
                                <div *ngIf="productOversubscribedMessage">{{productOversubscribedMessage}}</div> 
                            </mat-card-content>
                        </mat-card>
                        <mat-card *ngIf="!accessRestricted">
                            <mat-card-content *ngIf="!isReadable()" class="warning-panel">
                                <div fxLayout="row">
                                    <mat-icon class="warning-icon">priority_high</mat-icon><h3 style="margin-top:0px;">Permission Required</h3>
                                </div>
                                You do not have permission to read the content of this topic. Please contact your site administrator.
                            </mat-card-content>
                            <mat-card-content *ngIf="noTouchingFullText">
                                <div fxLayout="column" fxLayoutAlign="center center">
                                    <spinner></spinner>
                                </div> 
                            </mat-card-content>
                            <mat-card-content *ngIf="isReadable() && !showTags && !noTouchingFullText" class="main-contents" [ngStyle]="{'opacity' : (topic && !itemContext.item?.visible && !isSource) ? 0.5 : .99}">
                                <div *ngIf="itemContext && itemContext.item && !itemContext.item.oembed" [innerHtml]="fullTextDescription || itemContext?.item?.defaultDescription | safeHtml"
                                     style="display: inline-block;"></div>
                                <div *ngIf="itemContext && itemContext.item && itemContext.item.oembed" [innerHtml]="itemContext.item.oembed"
                                     style="display: inline-block;"></div>
                                <div style="padding-top: 25px; padding-bottom: 10px;">
                                    <mat-divider></mat-divider>
                                </div>
                                <more-like-this *ngIf="currentUser() && isReadable() && !curating" [item]="itemContext.item"></more-like-this>
                            </mat-card-content>
                            <mat-card-content *ngIf="isReadable() && showTags" 
                                            class="main-contents" 
                                            style="padding-top: 0px; padding-bottom: 0px;">
                                <div fxLayout="row">
                                    <button mat-button color="primary" 
                                            [disabled]="!selectedTags.length" 
                                            (click)="searchForTags()"
                                            style="padding-left: 0px;">
                                        <mat-icon>search</mat-icon>
                                        <span *ngIf="selectedTags.length">Search For Selected Tags</span>
                                        <span *ngIf="!selectedTags.length">Select Tags to Search</span>
                                    </button>
                                    <span fxFlex></span>
                                    <button mat-icon-button (click)="showTags = false" matTooltip="Hide Tags">
                                        <mat-icon>clear</mat-icon>
                                    </button>
                                </div>
                                <span *ngFor="let type of tagTypes(itemContext.item)">
                                    <h4 style="margin-bottom: 2px; margin-top: 15   px;">{{tagDefs[type].label}}</h4>
                                    <mat-chip-list>
                                        <mat-chip *ngFor="let tag of tagsFor(type, itemContext.item)"
                                                (click)="toggleTag(tag)"
                                                [ngClass]="tagDefs[tag.type].cls"
                                                [matTooltip]="(tagSelected(tag) ? 'Unselect ' : 'Select ') + tag.tagText">
                                            {{tag.tagText}}
                                            <mat-icon *ngIf="tagSelected(tag)" style="padding-left: 8px;">done</mat-icon>
                                        </mat-chip>
                                    </mat-chip-list>
                                </span>
                            </mat-card-content>
                        </mat-card>
                    </span>
                    <span *ngIf="editItem">
                        <item-editor [itemId]="itemContext.item.id" 
                                     [topicId]="topic?.id || itemContext.item.streamId" 
                                     (finished)="editComplete($event)"></item-editor>
                    </span>
                </div>
            </div>
        </div>
    `})
export class ItemViewerComponent implements OnChanges, OnDestroy {
    cleanupTime = new Subject<void>();
    @Input() itemContext: ItemContextModel;
    @Input() isSource: boolean = false;
    @Input() curating: boolean = false;
    @Output() close = new EventEmitter<void>();
    tenant: any;
    source: any;
    topic: any;
    context: string;
    contextSubscription: Subscription;
    fullTextError: boolean;
    fullTextDescription: string;
    noTouching: boolean = true;
    noTouchingFullText = false;
    isDeleteable = false;
    accessRestricted = false;
    isSubscribable = false;
    postedSourceContextEvents = false;
    productOversubscribedMessage: string;
    prodSource: any;
    swipeCoord: [number, number];
    swipeTime: number;
    processingLike: any;
    showTags: boolean;
    tagDefs: any;
    selectedTags = [];
    isEditable: boolean;
    editItem: boolean;
    canEditTopic: boolean;

    constructor(private router: Router,
                private route: ActivatedRoute,
                private sanitizer: DomSanitizer,
                private dialog: MatDialog,
                private snackBar: MatSnackBar,
                private eventService: EventService,
                private streamService: StreamService,
                private tenantService: TenantService,
                private itemService: ItemService,
                private permissionService: PermissionService,
                private userService: UserService,
                private media: MediaObserver){}

    isSmall() {
        return this.media.isActive('sm') || this.media.isActive('xs');
    }

    currentUser() {
        return this.userService.getCurrentUser();
    }

    safeEventCapture(label, context?) {
        if(this.source){
            this.eventService.postStreamItemEvent(label, { context }, this.itemContext.item, this.source);
        }else if(this.itemContext && this.itemContext.item && (this.topic && this.topic.type !== 'PRODUCT_SOURCE')){
            this.streamService.getStream(this.itemContext.item.streamOriginId).pipe(first())
            .subscribe(source => this.eventService.postStreamItemEvent(label, {context}, this.itemContext.item, source))
        }else if(this.itemContext && this.itemContext.item && (this.topic && this.topic.type === 'PRODUCT_SOURCE')){
            this.eventService.postStreamItemEvent(label, {context}, this.itemContext.item, this.topic);
        }
    };

    isReadable(): boolean {
        if (this.prodSource) {
            return this.permissionService.currentUserHasPermission({ action: 'VIEW_STREAM', streamId: this.prodSource.id})
        } else {
            return this.itemService.isReadable(this.topic, this.itemContext.item);
        }
    }

    postTopicOrSourceEvent(eventName, context?): void {
        if (this.topic) {
            this.eventService.postStreamItemEvent(eventName, { context }, this.itemContext.item, this.topic);
        } else {
            this.safeEventCapture(eventName, context);
        }
    }

    externalLinkClicked(): void {
        this.postTopicOrSourceEvent('item_topic_link_clicked', 'clientItemViewTitle');
        this.postTopicOrSourceEvent('item_link_clicked', 'clientItemViewTitle');
        this.safeEventCapture('item_source_link_clicked', 'clientItemViewTitle');
    };

    toggleSaved() {
        return this.itemService.toggleItemSaved(this.itemContext.item);
    }

    showFullTextLink(): boolean { 
        return this.itemContext.item && 
        !!this.itemContext.item.link && 
        !this.itemContext.item.linkResolverLink && 
        this.itemContext.item.link !== '#' &&
        this.itemContext.item.type !== 'TWEET';
    }

    fullTextLinkDisabled(): boolean {
        return !!(this.fullTextError || this.fullTextDescription);
    }

    getFullArticleText() {
        this.noTouchingFullText = true;
        this.itemService.getFullText(this.itemContext.item.linkResolverLink || this.itemContext.item.link)
        .pipe(tap(() => this.noTouchingFullText = false))
        .subscribe(results => {
            if(results.objects && results.objects.length && results.objects[0].html){
                this.fullTextDescription = results.objects[0].html;
                this.safeEventCapture('item_full_article_text_displayed');
            } else if(results.error){
                this.fullTextError = true;
            }
        },
        () => {
            this.fullTextError = true;
            this.noTouchingFullText = false;
        });
    }

    emailShare() {
        return this.itemService.shareItemByEmail(this.itemContext.item, this.topic);
    }

    toggleLikedBy() {
        if (!this.processingLike && this.itemContext && this.itemContext.item) {
            this.processingLike = true;
            this.itemService.toggleLikedBy(this.itemContext.item).pipe(first()).subscribe(() => this.processingLike = false);
        }
    }

    get canCopy(): boolean {
        return !this.accessRestricted && this.permissionService.currentUserIsStreamManager();
    }

    processContext(ctx, isRestricted: boolean = false, isSubscribable: boolean = false) {
        this.itemContext = ctx;
        
        if (this.itemContext.item.type === 'TWEET' && !this.itemContext.item.oembed) {
            this.itemService.getItemOembed(this.itemContext.item.link).pipe(first())
            .subscribe(oembed => {
                this.itemContext.item.oembed = this.sanitizer.bypassSecurityTrustHtml(oembed.html);
                if ((<any>window).twttr.ready()) {
                    setTimeout(() => (<any>window).twttr.widgets.load()); 
                }
            });
        } else if (this.itemContext.item.type === 'TWEET' && this.itemContext.item.oembed) {
            if ((<any>window).twttr.ready()) {
                setTimeout(() => (<any>window).twttr.widgets.load());
            }
        }

        this.topic = this.itemContext.topic;
        this.canEditTopic = this.topic &&
                this.topic.tenantId &&
                this.topic.tenantId === this.tenant.id &&
                this.topic.type === 'COLLECTION' &&
                this.permissionService.currentUserCanManageStream(this.topic);
        this.postSourceContextEvents();
        this.noTouching = false;
        this.accessRestricted = isRestricted;
        this.isSubscribable = isSubscribable;
        
        this.isDeleteable = false;
        this.isEditable = false;
        if (['COPY', 'EMAIL', 'MANUAL', 'SEARCH'].includes(this.itemContext.item.type)) {
            this.streamService.getStream(this.itemContext.item.streamOriginId)
            .subscribe(stream => {
                const streamId = this.itemContext.item.streamId; 
                const streamOwnerId = stream.ownerId;
                const userId = this.userService.getCurrentUser() ? this.userService.getCurrentUser().id : undefined;
                
                this.isEditable = ['COPY', 'MANUAL'].includes(this.itemContext.item.type) && this.permissionService.currentUserHasPermission({  action: 'MANAGE_STREAM', streamId, streamOwnerId, userId });
                this.isDeleteable = this.permissionService.currentUserHasPermission({  action: 'DELETE_ITEMS', streamId, streamOwnerId, userId });
            });
        }

        if (!isRestricted) {
            if (this.itemContext && this.itemContext.item && !this.itemContext.item.readByUser) {
                this.itemService.markItemRead(this.itemContext.item.id).pipe(first()).subscribe(result => this.itemContext.item.readByUser = true);
            }
            if ((!ctx.item.tenantId || ctx.item.tenantId === this.tenant.id) && 
                (!this.topic || (this.topic && this.topic.type !== 'PRODUCT_SOURCE'))) {
                this.streamService.getStream(ctx.item.streamOriginId).pipe(first()).subscribe(source => this.processSource(source, ctx));
            } else {
                this.processSource(this.prodSource, ctx);
            }
        } else {
            this.postTopicOrSourceEvent('attempted_to_view_item');
        }
    }

    processSource(source, ctx) {
        this.source = source;
        const sourceParam = this.route.snapshot.queryParams.source;
        this.eventService.postStreamItemEvent('item_viewed_topic', { source: sourceParam || this.context }, ctx.item, ctx.topic || this.source);
        this.eventService.postStreamItemEvent('item_viewed', { source: sourceParam || this.context }, ctx.item, ctx.topic || this.source);
        this.eventService.postStreamItemEvent('item_viewed_source', { source: sourceParam || this.context }, ctx.item, this.source || this.topic);
        this.postSourceContextEvents();
    }

    subscribeToProdSource() { 
        this.streamService.addStreamRole(this.userService.getCurrentUser().id, this.prodSource.id, 'STREAM_SUBSCRIBER')
        .pipe(
            mergeMap(() => this.userService.refetchUser()),
            first()
        ).subscribe(() => {
            this.prodSource.userHasStreamSubscriberRole = true;
            this.topic.userHasStreamSubscriberRole = true;
            this.processContext(this.itemContext, false);
        });
    }

    tagSelected(tag): boolean {
        return this.selectedTags.includes(tag); 
    }

    toggleTag(tag): void {
        if (this.selectedTags.includes(tag)) {
            this.selectedTags.splice(this.selectedTags.indexOf(tag), 1);
        } else {
            this.selectedTags.push(tag);
        }
    }

    searchForTags() {
        const tags = this.selectedTags.map(tag => `${tag.tagText}__${tag.type}`).join(',');
        this.router.navigate(['/search'], { queryParams: { tags } });
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (!this.tagDefs) {
            this.tagDefs = this.itemService.tagDefs;
            this.tenantService.getUnauthTenantInfo()
            .subscribe(tenant => {
                this.tenant = tenant;
                this.productOversubscribedMessage = tenant.settings.productOversubscribedMessage;        
            });
        }
        if (changes.itemContext && changes.itemContext.currentValue) {
            if (changes.itemContext.currentValue.item) {
                const ctx = changes.itemContext.currentValue; 
                if (ctx.item.tenantId && this.userService.getCurrentUser() && ctx.item.tenantId !== this.userService.getCurrentUser().tenantId && this.tenant.settings.limitProductSources === 'true') {
                    this.streamService.getProductSource(ctx.item.streamId).pipe(first())
                    .subscribe(prodSource => {
                        this.prodSource = prodSource;
                        this.processContext(ctx, 
                                            !prodSource.userHasStreamSubscriberRole, 
                                            prodSource.maxStreamSubscribers === undefined || prodSource.streamSubscribersCount < prodSource.maxStreamSubscribers); 
                    });
                } else {
                    this.processContext(ctx);                        
                }
            } else {
                this.noTouching = false;
            }
        }
    }

    postSourceContextEvents(): void {
        if (this.context && this.itemContext && this.topic && this.source && !this.postedSourceContextEvents) {
            const eventsToPost = [
                { name: 'item_link_clicked', stream: this.topic },
                { name: 'item_topic_link_clicked', stream: this.topic },
                { name: 'item_source_link_clicked', stream: this.source }
            ];
            _.each(eventsToPost, event => {
                this.eventService.postEvent(event.name, {
                    source: this.context,
                    item: this.itemContext.item,
                    stream: event.stream
                });
            });
            this.postedSourceContextEvents = true;
        }
    }

    tagTypes(item) {
        return item.tags?.map(tag => tag.type).filter((item, i, ar) => ar.indexOf(item) === i).sort() || [];
    } 
    
    tagsFor(type, item) {
        return item.tags.filter(tag => tag.type === type).sort((a, b) => {
            const aText = a.tagText.toLowerCase();
            const bText = b.tagText.toLowerCase();
            if (aText < bText) {
                return -1;
            } else if (aText > bText) {
                return 1;
            } else {
                return 0;
            }
        });
    }

    deleteItem(): void {
        this.dialog.open(ConfirmationDialogComponent, { 
            data: {
                title: `Delete this item from ${this.itemContext.item.streamOriginTitle}?`, 
                actionLabel: 'Delete'}
            }).afterClosed()
            .pipe(
                first(),
                filter(doIt => doIt),
                flatMap(() => this.itemService.deleteItem(this.itemContext.item.streamId, this.itemContext.item.id))
            )
            .subscribe(() => {
                this.snackBar.open(`item has been deleted.`, 'OK', { duration: 3000});
                this.itemService.itemContextChanged.next(this.itemContext.item.id);
                this.close.emit();
            });
    }

    copy(): void {
        this.dialog.open(CopyItemDialogComponent, { data: this.itemContext.item.id }).afterClosed()
        .pipe(filter(res => !!res))
        .subscribe(topicId => {
            this.itemService.focusedItem.next(undefined);
            this.itemService.focusedItemEdit.next({ sourceItemId: this.itemContext.item.id, topicId });
        });
    }

    editComplete(item: any) {
        if (!item) {
            this.itemService.itemContextChanged.next(item);
            this.close.emit();
        } else {
            Object.assign(this.itemContext.item, item);
            this.editItem = false;
        }
    }

    toggleVisibility() {
        this.itemContext.item.visible = !this.itemContext.item.visible;
        const patch = this.topic.curated ? { exposed: this.itemContext.item.visible } : { hidden: !this.itemContext.item.visible };
        this.itemService.patchItem(this.topic.id, this.itemContext.item.id, patch).subscribe();
    }

    toggleCurationItem() {
        this.itemService.toggleCurationItem(this.itemContext.item, this.topic).subscribe();
    }
    
    adjustedTooltip(tooltip): string {
        return `${tooltip}${this.currentUser() ? '' : ' - join to activate'}`;
    }

    ngOnDestroy() {
        this.cleanupTime.next();
        this.cleanupTime.complete();
        if (this.contextSubscription) {
            this.contextSubscription.unsubscribe();
        }
    };
}
